import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../../config/api';

// ─── Formatters ──────────────────────────────────────────────────────────────
const fmtUSD = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(parseFloat(value) || 0);

const fmtSource = (source) => {
  if (source === 'vapi' || source === 'vapi_call') return 'Phone Call';
    if (source === 'business_chat' || source === 'realtime_voice') return 'Business Assistant';
  if (!source) return 'Unknown';
  return source.charAt(0).toUpperCase() + source.slice(1).replace(/_/g, ' ');
};

const fmtDelta = (current, prior) => {
  if (!prior || prior === 0) return null;
  const pct = ((current - prior) / prior) * 100;
  return { pct: Math.abs(pct).toFixed(1), up: pct >= 0 };
};

// ─── Inline SVG Icons ────────────────────────────────────────────────────────
const IconDollar = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23"/>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
  </svg>
);

const IconCalendar = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const IconActivity = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);

const IconHash = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <line x1="4" y1="9" x2="20" y2="9"/>
    <line x1="4" y1="15" x2="20" y2="15"/>
    <line x1="10" y1="3" x2="8" y2="21"/>
    <line x1="16" y1="3" x2="14" y2="21"/>
  </svg>
);

const IconCreditCard = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
    <line x1="1" y1="10" x2="23" y2="10"/>
  </svg>
);

const IconChevronRight = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

const IconArrowUp = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5"/>
    <polyline points="5 12 12 5 19 12"/>
  </svg>
);

const IconArrowDown = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <polyline points="19 12 12 19 5 12"/>
  </svg>
);

// ─── Period config ────────────────────────────────────────────────────────────
const PERIODS = [
  { key: '7d',  label: '7D' },
  { key: '30d', label: '30D' },
  { key: 'mtd', label: 'MTD' },
  { key: 'all', label: 'All' },
];

const PERIOD_LABELS = {
  '7d':  'Last 7 Days',
  '30d': 'Last 30 Days',
  'mtd': 'Month to Date',
  'all': 'All Time',
};

const getPeriodBounds = (key) => {
  const now = new Date();
  switch (key) {
    case '7d': {
      const from = new Date(now); from.setDate(from.getDate() - 7);
      const priorFrom = new Date(now); priorFrom.setDate(priorFrom.getDate() - 14);
      const priorTo = new Date(from);
      return { from, priorFrom, priorTo };
    }
    case '30d': {
      const from = new Date(now); from.setDate(from.getDate() - 30);
      const priorFrom = new Date(now); priorFrom.setDate(priorFrom.getDate() - 60);
      const priorTo = new Date(from);
      return { from, priorFrom, priorTo };
    }
    case 'mtd': {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      const priorFrom = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const priorTo = new Date(from);
      return { from, priorFrom, priorTo };
    }
    default:
      return { from: null, priorFrom: null, priorTo: null };
  }
};

// ─── Compute stats ────────────────────────────────────────────────────────────
const computeStats = (conversations, periodKey) => {
  if (!conversations.length) return null;

  const { from, priorFrom, priorTo } = getPeriodBounds(periodKey);

  // inRange: for current period we check start <= date (no upper bound)
  // Conversations with no start_time are included when period='all', otherwise excluded
  const inCurrent = (date) => {
    if (!from) return true;         // period='all' → include everything
    if (!date) return false;        // no date and period is filtered → exclude
    return date >= from;
  };

  const inPrior = (date) => {
    if (!priorFrom || !priorTo) return false;
    if (!date) return false;
    return date >= priorFrom && date < priorTo;
  };

  let totalSpend = 0;
  let priorSpend = 0;
  let count = 0;
  let priorCount = 0;
  const bySource = {};

  conversations.forEach((c) => {
    const cost = parseFloat(c.cost) || 0;
    const start = c.start_time ? new Date(c.start_time) : null;
    const src = fmtSource(c.source);

    if (inCurrent(start)) {
      totalSpend += cost;
      count += 1;
      if (!bySource[src]) bySource[src] = { count: 0, cost: 0 };
      bySource[src].count += 1;
      bySource[src].cost += cost;
    }

    if (inPrior(start)) {
      priorSpend += cost;
      priorCount += 1;
    }
  });

  const avg = count > 0 ? totalSpend / count : 0;
  const priorAvg = priorCount > 0 ? priorSpend / priorCount : 0;

  const breakdown = Object.entries(bySource)
    .sort((a, b) => b[1].cost - a[1].cost)
    .map(([source, d]) => ({
      source,
      count: d.count,
      cost: d.cost,
      pct: totalSpend > 0 ? Math.round((d.cost / totalSpend) * 100) : 0,
    }));

  return {
    totalSpend,
    priorSpend,
    count,
    priorCount,
    avg,
    priorAvg,
    breakdown,
    channelCount: breakdown.length,
    deltaTotal: fmtDelta(totalSpend, priorSpend),
    deltaAvg: fmtDelta(avg, priorAvg),
    deltaCount: fmtDelta(count, priorCount),
  };
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const SkeletonKpi = () => (
  <div className="spend-kpi-cell">
    <div className="spend-skel spend-skel--sm" style={{ width: '55%' }} />
    <div className="spend-skel spend-skel--lg" style={{ width: '72%', marginTop: 6 }} />
  </div>
);

// ─── Delta badge ──────────────────────────────────────────────────────────────
const Delta = ({ delta }) => {
  if (!delta) return null;
  return (
    <span className={`spend-delta ${delta.up ? 'spend-delta--up' : 'spend-delta--down'}`}>
      {delta.up ? <IconArrowUp /> : <IconArrowDown />}
      {delta.pct}%
    </span>
  );
};

// ─── Period selector ──────────────────────────────────────────────────────────
const PeriodTabs = ({ period, onChange }) => (
  <div className="spend-period-tabs">
    {PERIODS.map((p) => (
      <button
        key={p.key}
        className={`spend-period-tab${period === p.key ? ' is-active' : ''}`}
        onClick={() => onChange(p.key)}
      >
        {p.label}
      </button>
    ))}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
const SpendingWidget = ({ creditBalance = 0 }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const response = await fetch(`${API_BASE_URL}/api/conversations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (data.status === 'success') {
          setConversations(data.conversations || []);
        } else {
          setError('Unable to load conversation data.');
        }
      } catch (err) {
        setError(`Could not load spending data. ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const stats = useMemo(
    () => computeStats(conversations, period),
    [conversations, period]
  );

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="dash-card spend-card">
        <div className="spend-header">
          <div className="spend-header-left">
            <span className="spend-eyebrow">SPENDING OVERVIEW</span>
            <div className="spend-skel spend-skel--xl" style={{ width: 160, marginTop: 6 }} />
          </div>
        </div>
        <div className="spend-kpi-strip">
          <SkeletonKpi /><SkeletonKpi /><SkeletonKpi /><SkeletonKpi />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
          <div className="spend-skel spend-skel--sm" style={{ width: '40%' }} />
          <div className="spend-skel spend-skel--md" style={{ width: '100%' }} />
          <div className="spend-skel spend-skel--md" style={{ width: '85%' }} />
          <div className="spend-skel spend-skel--md" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="dash-card spend-card">
        <div className="spend-header">
          <div className="spend-header-left">
            <span className="spend-eyebrow">SPENDING OVERVIEW</span>
          </div>
        </div>
        <p className="spend-state-msg spend-state-msg--error">{error}</p>
      </div>
    );
  }

  // ── No data at all ─────────────────────────────────────────────────────────
  if (!stats) {
    return (
      <div className="dash-card spend-card">
        <div className="spend-header">
          <div className="spend-header-left">
            <span className="spend-eyebrow">SPENDING OVERVIEW</span>
            <span className="spend-primary-value">$0.00</span>
          </div>
          <PeriodTabs period={period} onChange={setPeriod} />
        </div>
        <p className="spend-state-msg">No conversations recorded yet. Costs will appear here once your AI starts handling interactions.</p>
      </div>
    );
  }

  // ── No activity in selected period (but data exists in other periods) ──────
  if (stats.count === 0) {
    return (
      <div className="dash-card spend-card">
        <div className="spend-header">
          <div className="spend-header-left">
            <span className="spend-eyebrow">SPENDING OVERVIEW</span>
            <span className="spend-primary-value">$0.00</span>
          </div>
          <PeriodTabs period={period} onChange={setPeriod} />
        </div>
        <p className="spend-state-msg">
          No activity recorded for <strong>{PERIOD_LABELS[period]}</strong>.{' '}
          Try selecting a different period or <strong>All</strong> to view your full history.
        </p>
      </div>
    );
  }

  // ── Full view ──────────────────────────────────────────────────────────────
  const kpis = [
    {
      icon: <IconDollar />,
      label: 'Total Spend',
      value: fmtUSD(stats.totalSpend),
      delta: stats.deltaTotal,
    },
    {
      icon: <IconCalendar />,
      label: PERIOD_LABELS[period],
      value: fmtUSD(stats.totalSpend),
      delta: null,
    },
    {
      icon: <IconActivity />,
      label: 'Avg / Conversation',
      value: fmtUSD(stats.avg),
      delta: stats.deltaAvg,
    },
    {
      icon: <IconHash />,
      label: 'Conversations',
      value: stats.count.toLocaleString(),
      delta: stats.deltaCount,
    },
  ];

  return (
    <div className="dash-card spend-card">
      {/* Header */}
      <div className="spend-header">
        <div className="spend-header-left">
          <span className="spend-eyebrow">SPENDING OVERVIEW</span>
          <span className="spend-primary-value">{fmtUSD(stats.totalSpend)}</span>
        </div>
        <PeriodTabs period={period} onChange={setPeriod} />
      </div>

      {/* KPI strip */}
      <div className="spend-kpi-strip">
        {kpis.map((k) => (
          <div key={k.label} className="spend-kpi-cell">
            <div className="spend-kpi-label-row">
              <span className="spend-kpi-icon">{k.icon}</span>
              <span className="spend-kpi-label">{k.label}</span>
            </div>
            <div className="spend-kpi-value">{k.value}</div>
            {k.delta && <Delta delta={k.delta} />}
          </div>
        ))}
      </div>

      {/* Channel breakdown */}
      {stats.breakdown.length > 0 && (
        <div className="spend-breakdown">
          <div className="spend-section-label-row">
            <span className="spend-section-label">By Channel</span>
            <span className="spend-section-hint">
              {stats.channelCount} channel{stats.channelCount !== 1 ? 's' : ''} &middot; {PERIOD_LABELS[period]}
            </span>
          </div>
          <table className="spend-table">
            <thead>
              <tr>
                <th className="spend-th spend-th--left">Channel</th>
                <th className="spend-th spend-th--right">Conversations</th>
                <th className="spend-th spend-th--right">Cost</th>
                <th className="spend-th spend-th--right">Share</th>
              </tr>
            </thead>
            <tbody>
              {stats.breakdown.map((row) => (
                <tr key={row.source} className="spend-tr">
                  <td className="spend-td spend-td--source">{row.source}</td>
                  <td className="spend-td spend-td--num">{row.count.toLocaleString()}</td>
                  <td className="spend-td spend-td--num">{fmtUSD(row.cost)}</td>
                  <td className="spend-td spend-td--share">
                    <div className="spend-share-wrap">
                      <div className="spend-mini-bar">
                        <div className="spend-mini-bar-fill" style={{ width: `${row.pct}%` }} />
                      </div>
                      <span className="spend-share-pct">{row.pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      <div className="spend-footer">
        <span className="spend-credit">
          <IconCreditCard />
          Available credit &nbsp;<strong>{fmtUSD(creditBalance)}</strong>
        </span>
        <a className="spend-view-link" href="/conversations">
          View detailed activity <IconChevronRight />
        </a>
      </div>
    </div>
  );
};

export default SpendingWidget;
